import { reactive } from 'vue';
import { onContentReplacing } from './contentLifecycle.js';

// Shared between the search bar widget (mounted in the header on every opted-in
// page) and SearchApp.vue (the search results page) - two separate `createApp()`
// roots that share this via plain ES module import, same pattern as
// modeStore.js/basketStore.js.
//
// The widget navigates to the search page on submit UNLESS it's already on it,
// where it instead pushes through here for an in-place re-search - on every field
// change, not only on submit. isResultsPage is how the widget knows which one it's
// on; SearchApp.vue sets it during its own setup(), before the page has finished
// loading, so it's always correct by the time a user could touch the form.
// appliedType is the return channel: the results page's own type filter (TypeFilter.vue's
// chips) sets it so the bar's Experience select reflects the same choice. Without it the
// two drift apart - the chips rewrite ?type= while the bar, which reads the URL only once
// at setup, carries on showing the type the customer arrived with.
//
// undefined, not null, as the initial value: null is a real applied type ("All"), and the
// bar has to be able to tell "nothing has applied a type yet" from "All was chosen". The
// two roots mount in no guaranteed order, so the bar reads this immediately on setup and
// must not clobber its own URL-seeded value when the results page has yet to speak.
export const state = reactive({
    isResultsPage: false,
    isLocationScopedPage: false,
    submitted: null,
    appliedType: undefined,

    // What kind of page is on screen, read from #search-bar-widget by readPageKind(). Held
    // here rather than as SearchBar.vue props because a content swap changes the page under
    // a bar that is never remounted, and props cannot be re-seeded. The header is never
    // swapped, so after a swap the live widget's attributes describe the page the customer
    // STARTED on - these are read from the fetched page instead.
    //
    // The defaults are what a page with no widget gets: no location, open, and no swap of
    // either kind.
    pageLocationId: null,
    initiallyOpen: true,
    swapsContentForLocation: false,
    swapsForGift: false,
    giftingUrl: null,

    // Where a Book press on /gifting takes the customer: the homepage of the spa the bar
    // holds, or null where that spa has none (modeStore.js then falls back to '/').
    // Published by SearchBar.vue, which owns the spa list and the one lookup of a spa's
    // homepage path, so modeStore.js needs no second copy of either.
    bookingHomePath: null,
});

// Seeds the page-kind state above from a #search-bar-widget element: the live one on page
// load, and the fetched document's after every content swap (SearchBar.vue). Each attribute
// is compared against a string, not read for truthiness, since Boolean('false') is true, and
// defaults the way partials/header.blade.php does when the attribute is absent.
export function readPageKind(el) {
    if (! el) {
        return;
    }

    // Number('') is 0 on a page belonging to no location; || null keeps "none" a real null.
    state.pageLocationId = Number(el.dataset.pageLocationId) || null;
    state.initiallyOpen = el.dataset.initiallyOpen !== 'false';
    state.swapsContentForLocation = el.dataset.swapsContentForLocation === 'true';
    state.swapsForGift = el.dataset.swapsForGift !== 'false';
    state.giftingUrl = el.dataset.giftingUrl || null;
}

export function activateResultsPage() {
    state.isResultsPage = true;
}

// Set by a page whose own server-rendered content is priced against the customer's chosen
// location - the product page (ProductActions.vue), where the heading, price, member price,
// duration and location label all come from it. Such a page has to reload when the location
// changes, or the bar would show one spa while the figures beside it described another.
//
// Same handshake as activateResultsPage(): the bar cannot know which kind of page it is
// mounted on, so the page tells it during setup, before a customer could touch the form.
export function activateLocationScopedPage() {
    state.isLocationScopedPage = true;
}

// The flag above is one-way, and every surface that sets it is an island in the page
// content, so it has to go with the content it describes. Left standing, a Where change on
// a swapped-in /gifting would reload it as if it were the product page it replaced. Any
// island arriving with the new content sets it again when it mounts, which is after this.
//
// isResultsPage is deliberately not reset: /search is never swapped out, and SearchApp is
// not an island, so nothing would set it again.
onContentReplacing(() => {
    state.isLocationScopedPage = false;
});

export function submitSearch(params) {
    state.submitted = { ...params };
}

// Called by the results page when its type filter changes, including when it drops a type
// the URL asked for. Takes null for "All"; see appliedType above for why that is not
// the same as the initial undefined.
export function applyType(type) {
    state.appliedType = type ?? null;
}
